import React from 'react';
import { Drawer, Box, Typography, IconButton, TextField, Button, Stack, Chip, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Popover, Paper, InputBase } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as TasksSvg } from '../../assets/images/tasks.svg';
import { listMyTasks, createMyTask, completeMyTask, deleteMyTask, updateMyTask } from '../../services/localTasksService';
import type { LocalTaskItem as TaskItem } from '../../services/localTasksService';

interface Props {
  open: boolean;
  onClose: () => void;
  zIndex?: number;
  onEntered?: () => void;
}

const TasksDrawer: React.FC<Props> = ({ open, onClose, zIndex, onEntered }) => {
  const { user } = useAuth();
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [active, setActive] = React.useState<TaskItem[]>([]);
  const [completed, setCompleted] = React.useState<TaskItem[]>([]);
  const [newTitle, setNewTitle] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [composerVisible, setComposerVisible] = React.useState(false);
  const [dueDate, setDueDate] = React.useState<string | null>(null); // 'YYYY-MM-DD'
  const dateInputRef = React.useRef<HTMLInputElement | null>(null);
  // Lokalna lista zadań – jedna per użytkownik
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuTask, setMenuTask] = React.useState<TaskItem | null>(null);
  const [inlineDateTask, setInlineDateTask] = React.useState<TaskItem | null>(null);
  const taskDateInputRef = React.useRef<HTMLInputElement | null>(null);
  const [editingTaskId, setEditingTaskId] = React.useState<string | null>(null);
  const [editingTitle, setEditingTitle] = React.useState<string>('');
  const editInputRef = React.useRef<HTMLInputElement | null>(null);
  const [completedCollapsed, setCompletedCollapsed] = React.useState<boolean>(true);
  const [datePopoverAnchor, setDatePopoverAnchor] = React.useState<HTMLElement | null>(null);
  const [datePopoverPos, setDatePopoverPos] = React.useState<{ top: number; left: number } | null>(null);
  const popoverInputRef = React.useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = React.useState<string>('');
  const [dragging, setDragging] = React.useState<{ list: 'active'|'completed'; id: string } | null>(null);
  const [dragOverId, setDragOverId] = React.useState<string | null>(null);
  const [confettiTaskId, setConfettiTaskId] = React.useState<string | null>(null);
  const openByKeyboardRef = React.useRef<boolean>(false);
  const lastInputTypeRef = React.useRef<'keyboard'|'pointer'|null>(null);
  const [newlyAddedId, setNewlyAddedId] = React.useState<string | null>(null);
  const [keyboardPickerOpen, setKeyboardPickerOpen] = React.useState<boolean>(false);
  const keyboardChangedRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    const onKey = () => { lastInputTypeRef.current = 'keyboard'; };
    const onPointer = () => { lastInputTypeRef.current = 'pointer'; };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, []);
  // Potwierdzanie Enterem, gdy picker otwarty z klawiatury
  React.useEffect(() => {
    if (!keyboardPickerOpen) return;
    const onDocKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        try { (e as any).stopImmediatePropagation?.(); } catch {}
        e.stopPropagation();
        e.preventDefault();
        const value = (dateInputRef.current as HTMLInputElement | null)?.value || '';
        if (value) {
          const hasTitle = (newTitle || '').trim().length > 0;
          if (hasTitle) { handleAdd(value); } else { setDueDate(value); }
        }
        setKeyboardPickerOpen(false);
        openByKeyboardRef.current = false;
      } else if (e.key === 'Escape') {
        setKeyboardPickerOpen(false);
        openByKeyboardRef.current = false;
      }
    };
    document.addEventListener('keydown', onDocKey, true);
    return () => document.removeEventListener('keydown', onDocKey, true as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardPickerOpen]);

  const isDragEnabled = React.useMemo(() => {
    return (search || '').trim().length === 0 && !editingTaskId && !composerVisible;
  }, [search, editingTaskId, composerVisible]);

  function handleDragStart(list: 'active'|'completed', taskId: string, e: React.DragEvent) {
    if (!isDragEnabled) return;
    try { e.dataTransfer.effectAllowed = 'move'; } catch {}
    setDragging({ list, id: taskId });
  }
  function handleDragOver(list: 'active'|'completed', taskId: string, e: React.DragEvent) {
    if (!isDragEnabled) return;
    e.preventDefault();
    setDragOverId(taskId);
  }
  function handleDragEnd() {
    setDragging(null);
    setDragOverId(null);
  }
  function handleDrop(list: 'active'|'completed', targetId: string, e: React.DragEvent) {
    if (!isDragEnabled) return;
    e.preventDefault();
    const src = dragging;
    setDragging(null);
    setDragOverId(null);
    if (!src || src.list !== list) return;
    if (src.id === targetId) return;
    if (list === 'active') {
      setActive(prev => {
        const from = prev.findIndex(t => t.id === src.id);
        const to = prev.findIndex(t => t.id === targetId);
        if (from < 0 || to < 0) return prev;
        const next = prev.slice();
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        return next;
      });
    } else {
      setCompleted(prev => {
        const from = prev.findIndex(t => t.id === src.id);
        const to = prev.findIndex(t => t.id === targetId);
        if (from < 0 || to < 0) return prev;
        const next = prev.slice();
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        return next;
      });
    }
  }

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [openList, doneList] = await Promise.all([
        listMyTasks({ showCompleted: false }),
        listMyTasks({ showCompleted: true })
      ]);
      setActive(openList.filter(t => t.status !== 'completed'));
      setCompleted(doneList.filter(t => t.status === 'completed'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Priorytet ESC: najpierw zamknij edytor, potem (globalnie) okno
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && composerVisible) {
        e.preventDefault();
        // zablokuj propagację także dla innych listenerów na tym samym elemencie
        try { (e as any).stopImmediatePropagation?.(); } catch {}
        e.stopPropagation(); // nie zamykaj od razu całego panelu – najpierw schowaj composer
        setComposerVisible(false);
        setNewTitle('');
        setDueDate(null);
      }
    };
    // użyj fazy capture na dokumencie, żeby przechwycić przed globalnym handlerem
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true as any);
  }, [open, composerVisible]);

  // Wystaw globalny znacznik, że „Moje zadania” są otwarte (do ewentualnych warunków w innych miejscach)
  React.useEffect(() => {
    try { (window as any).__unitrescoTasksOpen = !!open; } catch {}
    return () => { try { (window as any).__unitrescoTasksOpen = false; } catch {} };
  }, [open]);

  // Lokalna obsługa skrótu „d” wyłącznie w obrębie „Moje zadania”
  React.useEffect(() => {
    if (!open) return;
    const isTyping = (el: EventTarget | null) => {
      const t = el as HTMLElement | null;
      const tag = t?.tagName?.toLowerCase();
      return !!(t && (t.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select'));
    };
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if ((e.key === 'd' || e.key === 'D') && !isTyping(e.target)) {
        // Zablokuj globalne i stronowe handlery
        try { (e as any).stopImmediatePropagation?.(); } catch {}
        e.stopPropagation();
        e.preventDefault();
        // Otwórz edytor dodawania zadania (jak klik „Dodaj zadanie”)
        setComposerVisible(true);
        try { setTimeout(() => { (document.querySelector('[data-role=\"tasks-filter\"]') as HTMLInputElement | null)?.blur?.(); }, 0); } catch {}
      }
    };
    const onDocKeyUp = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'd' || e.key === 'D') {
        // Zablokuj globalne keyup (np. Header)
        try { (e as any).stopImmediatePropagation?.(); } catch {}
        e.stopPropagation();
        e.preventDefault();
      }
    };
    // Faza capture – przechwytujemy zanim dotrze do window/Layout/stron
    document.addEventListener('keydown', onDocKeyDown, true);
    document.addEventListener('keyup', onDocKeyUp, true);
    return () => {
      document.removeEventListener('keydown', onDocKeyDown, true as any);
      document.removeEventListener('keyup', onDocKeyUp, true as any);
    };
  }, [open]);

  // Wystaw globalny znacznik informujący topbar, że ESC ma być zblokowany dla panelu zadań gdy trwa dodawanie LUB edycja
  React.useEffect(() => {
    try { (window as any).__unitrescoTasksComposerVisible = open && (composerVisible || !!editingTaskId); } catch {}
    return () => { try { (window as any).__unitrescoTasksComposerVisible = false; } catch {} };
  }, [open, composerVisible, editingTaskId]);

  const toIsoFromYmd = (ymd: string) => {
    try {
      const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
      const dt = new Date(y, (m || 1) - 1, d || 1, 9, 0, 0); // 09:00 lokalnie – stabilnie dla Tasks
      return dt.toISOString();
    } catch {
      return undefined;
    }
  };

  const handleAdd = async (dueOverrideYmd?: string | null) => {
    if (!newTitle.trim()) return;
    const title = newTitle.trim();
    setErrorMsg(null);
    const ymd = typeof dueOverrideYmd !== 'undefined' ? (dueOverrideYmd || null) : (dueDate || null);
    const dueIso = ymd ? toIsoFromYmd(ymd) : undefined;
    // Optymistycznie pokaż nowe zadanie od razu
    const optimistic: TaskItem = {
      id: 'tmp-' + Date.now(),
      title,
      status: 'needsAction',
      due: dueIso || null,
      completed: null
    };
    setActive(prev => [optimistic, ...prev]);
    setNewlyAddedId(optimistic.id);
    setTimeout(()=> setNewlyAddedId((curr)=> curr === optimistic.id ? null : curr), 900);
    // Natychmiast schowaj composer – subiektywnie szybciej
    setNewTitle('');
    setComposerVisible(false);
    setDueDate(null);
    // Zapis w tle; po sukcesie podmień optymistyczny wpis lub przeładuj listę
    (async () => {
      try {
        const created = await createMyTask({ title, due: dueIso || null });
        // Spróbuj podmienić optymistyczną pozycję na faktycznie utworzoną
        const createdTask = created as TaskItem | undefined;
        if (createdTask && createdTask.id) {
          setActive(prev => prev.map(t => t.id === optimistic.id ? { ...t, ...createdTask } : t));
        } else {
          // Gdy brak danych – odśwież cicho w tle
          try { load(); } catch {}
        }
        try { window.dispatchEvent(new CustomEvent('tasks:changed')); } catch {}
      } catch (e: any) {
        // Wycofaj optymistyczny wpis i pokaż informację o błędzie
        setActive(prev => prev.filter(t => t.id !== optimistic.id));
        const serverMsg = e?.response?.data?.message || e?.message;
        setErrorMsg(serverMsg ? String(serverMsg) : 'Nie udało się dodać zadania. Spróbuj ponownie.');
      }
    })();
  };

  const handleComplete = async (task: TaskItem) => {
    // burst animation
    setBurstId(task.id);
    setTimeout(()=>setBurstId(null), 420);
    // confetti nad całym wierszem
    setConfettiTaskId(task.id);
    setTimeout(()=> setConfettiTaskId(null), 900);
    try {
      await completeMyTask(task.id);
      // optymistycznie przenieś do completed
      setActive(prev => prev.filter(t => t.id !== task.id));
      setCompleted(prev => [{ ...task, status: 'completed', completed: new Date().toISOString() }, ...prev]);
      try { window.dispatchEvent(new CustomEvent('tasks:changed')); } catch {}
    } catch {}
  };

  const Checkbox: React.FC<{ checked?: boolean; onClick?: () => void }> = ({ checked, onClick }) => (
    <Box
      component="img"
      onClick={onClick}
      sx={{ width: 17, height: 17, cursor: onClick ? 'pointer' : 'default' }}
      src={checked ? '/assets/topbar/check_checked.svg' : '/assets/topbar/check_empty.svg'}
      alt={checked ? 'checked' : 'unchecked'}
    />
  );
  const [burstId, setBurstId] = React.useState<string | null>(null);

  const formatDueYmd = (iso?: string | null) => {
    if (!iso) return null;
    try {
      // wejście może być YYYY-MM-DD lub ISO; zawsze zwróć YYYY-MM-DD
      const s = String(iso);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      return new Date(s).toISOString().slice(0,10);
    } catch { return null; }
  };

  const openMenu = (task: TaskItem) => (e: React.MouseEvent<HTMLElement>) => {
    setMenuTask(task);
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuTask(null); };

  const handleDelete = async () => {
    if (!menuTask) return;
    try {
      await deleteMyTask(menuTask.id);
      setActive(prev => prev.filter(t => t.id !== menuTask.id));
      setCompleted(prev => prev.filter(t => t.id !== menuTask.id));
      try { window.dispatchEvent(new CustomEvent('tasks:changed')); } catch {}
    } finally {
      closeMenu();
    }
  };

  const handleEdit = async () => {
    if (!menuTask) return;
    setEditingTaskId(menuTask.id);
    setEditingTitle(menuTask.title || '');
    closeMenu();
    setTimeout(()=>editInputRef.current?.focus(), 0);
  };

  const submitEdit = async () => {
    if (!editingTaskId) { setEditingTaskId(null); return; }
    const title = (editingTitle || '').trim();
    if (title.length === 0) { setEditingTaskId(null); return; }
    try {
      await updateMyTask(editingTaskId, { title });
      setActive(prev => prev.map(t => t.id===editingTaskId ? { ...t, title } : t));
      setCompleted(prev => prev.map(t => t.id===editingTaskId ? { ...t, title } : t));
      try { window.dispatchEvent(new CustomEvent('tasks:changed')); } catch {}
    } finally {
      setEditingTaskId(null);
    }
  };

  const applyDueForTask = async (task: TaskItem | null, value: string) => {
    if (!task || !value) return;
    try {
      const iso = toIsoFromYmd(value);
      await updateMyTask(task.id, { due: iso || null });
      setActive(prev => prev.map(t => t.id===task.id ? { ...t, due: iso || null } : t));
      setCompleted(prev => prev.map(t => t.id===task.id ? { ...t, due: iso || null } : t));
      try { window.dispatchEvent(new CustomEvent('tasks:changed')); } catch {}
    } catch {}
  };

  const openNativeDatePicker = (task: TaskItem, presetYmd?: string | null): boolean => {
    const input = taskDateInputRef.current;
    if (!input) {
      // awaryjnie – stwórz tymczasowy input w <body>
      const tmp = document.createElement('input');
      tmp.type = 'date';
      if (presetYmd) tmp.value = presetYmd;
      tmp.style.position = 'fixed';
      tmp.style.left = '0px';
      tmp.style.top = '0px';
      tmp.style.opacity = '0';
      document.body.appendChild(tmp);
      tmp.onchange = (ev: any) => {
        const value = String(ev?.target?.value || '');
        applyDueForTask(task, value);
        setInlineDateTask(null);
        try { document.body.removeChild(tmp); } catch {}
      };
      const hasShowPicker = !!(tmp as any).showPicker;
      try { (tmp as any).showPicker?.(); } catch {}
      try { tmp.focus(); } catch {}
      try { tmp.click(); } catch {}
      return hasShowPicker;
    }
    // ustaw wstępnie wartość (dla edycji)
    try { (input as any).value = presetYmd || ''; } catch {}
    // spróbuj natywnego showPicker (Chromium), w innym razie fokus+klik
    // WAŻNE: wywołuj synchronicznie w ramach aktywacji użytkownika (klik z menu),
    // bo setTimeout zdejmuje user-activation i przeglądarka zablokuje picker.
    const prevLeft = input.style.left;
    const prevTop = input.style.top;
    input.style.left = '0px';
    input.style.top = '0px';
    const hasShowPicker = !!(input as any).showPicker;
    try { (input as any).showPicker?.(); } catch {}
    try { input.focus(); } catch {}
    try { input.click(); } catch {}
    // po krótkiej chwili przywróć ukrycie
    setTimeout(() => {
      input.style.left = prevLeft || '-9999px';
      input.style.top = prevTop || '0px';
    }, 300);
    return hasShowPicker;
  };

  const handleAddDue = () => {
    if (!menuTask) return;
    setInlineDateTask(menuTask);
    const usedNative = openNativeDatePicker(menuTask, null);
    // ZAWSZE pokaż popover (dla pełnej niezawodności i natychmiastowego UI)
    try {
      if (menuAnchor) {
        const r = menuAnchor.getBoundingClientRect();
        setDatePopoverPos({ top: Math.round(r.bottom + 6), left: Math.round(r.right) });
      } else {
        setDatePopoverPos({ top: 120, left: 120 });
      }
      setDatePopoverAnchor(null);
    } catch {}
    // zamknij menu po zainicjowaniu pickera
    setTimeout(()=>closeMenu(), 0);
  };

  const handleEditDue = () => {
    if (!menuTask) return;
    setInlineDateTask(menuTask);
    const currentYmd = formatDueYmd(menuTask.due);
    const usedNative = openNativeDatePicker(menuTask, currentYmd);
    try {
      if (menuAnchor) {
        const r = menuAnchor.getBoundingClientRect();
        setDatePopoverPos({ top: Math.round(r.bottom + 6), left: Math.round(r.right) });
      } else {
        setDatePopoverPos({ top: 120, left: 120 });
      }
      setDatePopoverAnchor(null);
    } catch {}
    // zamknij menu po zainicjowaniu pickera
    setTimeout(()=>closeMenu(), 0);
  };

  const onPickDueForTask = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';
    const task = inlineDateTask;
    setInlineDateTask(null);
    await applyDueForTask(task, value);
  };
  
  // Chip „Od <inicjały>” – źródło zadania (przydzielający)
  const getAssigner = React.useCallback((notes?: string | null) => {
    if (!notes || typeof notes !== 'string') return null;
    if (!notes.startsWith('__ASSIGNER__')) return null;
    try {
      const [, uid, name] = notes.split(':', 4);
      const n = name ? decodeURIComponent(name) : '';
      const initials = (() => {
        const parts = (n || '').trim().split(/\s+/).filter(Boolean);
        const raw = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
        return raw.toUpperCase() || '?';
      })();
      return { userId: uid || '', name: n, initials };
    } catch { return null; }
  }, []);
  const AssignerChip: React.FC<{ name: string; initials: string }> = ({ name, initials }) => (
    <Tooltip title={`Od: ${name || '—'}`}>
      <Chip
        label={`Od ${initials}`}
        size="small"
        sx={{
          bgcolor: '#E6F7FF',
          color: '#020242',
          borderRadius: '4px',
          minHeight: 21,
          height: 21,
          '& .MuiChip-label': {
            px: 1,
            py: 0,
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            fontWeight: 400,
            lineHeight: '13px',
            color: '#020242'
          }
        }}
      />
    </Tooltip>
  );
  
  // Gdy fallback popover został otwarty – natychmiast pokaż systemowy kalendarz
  React.useEffect(() => {
    if (!(datePopoverAnchor || datePopoverPos)) return;
    const run = () => {
      const el = popoverInputRef.current;
      if (!el) return;
      try { (el as any).showPicker?.(); } catch {}
      try { el.focus(); } catch {}
      try { el.click(); } catch {}
    };
    // odczekaj 0ms aż Popover się zrenderuje
    const id = setTimeout(run, 0);
    return () => clearTimeout(id);
  }, [datePopoverAnchor, datePopoverPos]);

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      sx={{ zIndex: zIndex ?? ((theme) => theme.zIndex.drawer) }}
      SlideProps={{ onEntered: () => { try { onEntered?.(); } catch {} } } as any}
      onClose={(event, reason) => {
        // Blokuj zamykanie całego panelu ESC-em, jeśli aktywny jest edytor dodawania
        if (reason === 'escapeKeyDown' && composerVisible) {
          try { (event as any).preventDefault?.(); } catch {}
          setComposerVisible(false);
          setNewTitle('');
          setDueDate(null);
          return;
        }
        // Blokuj zamykanie panelu ESC, gdy trwa edycja tytułu – wyjdź tylko z edycji
        if (reason === 'escapeKeyDown' && editingTaskId) {
          try { (event as any).preventDefault?.(); } catch {}
          setEditingTaskId(null);
          setEditingTitle('');
          return;
        }
        onClose();
      }}
      ModalProps={{ disableEnforceFocus: true, disableAutoFocus: true, keepMounted: true, BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.35)' } } as any }}
      PaperProps={{ 
        ref: paperRef as any,
        sx: { width: 420, maxWidth: '100vw', minWidth: 320, top: 0, height: '100vh', borderRadius: 0, right: 0, left: 'auto', pointerEvents: 'auto', zIndex: zIndex ?? (theme => theme.zIndex.drawer) },
        onTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => { try { if ((e as any).propertyName === 'transform') onEntered?.(); } catch {} }
      }}
    >
      
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff', borderBottom: '1px solid #eee' }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TasksSvg width={20} height={20} />
            </Box>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>Moje zadania</Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button
            variant="contained"
            onClick={()=>setComposerVisible(true)}
            data-action="tasks:add"
            sx={{
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj zadanie
          </Button>
          <Paper
            elevation={0}
            sx={{
              px: 0,
              py: 0,
              ml: 1,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #ededed',
              height: 36,
              width: 260,
              '&:hover': { borderColor: '#B9B9B9' }
            }}
          >
            <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
            <InputBase
              placeholder="Szukaj w zadaniach"
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              onKeyDown={(e)=>{ if(e.key==='Escape'){ e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} } }}
              sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
              inputProps={{ 'aria-label': 'szukaj w zadaniach', 'data-role': 'tasks-filter' }}
            />
          </Paper>
        </Box>
      </Box>

      <Box sx={{ p: 2, pt: 1 }}>
        {composerVisible && (
          <Box sx={{ p: 1, borderRadius: '6px', backgroundColor: '#F5F5FF' }}>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField
              placeholder="Nazwa zadania"
              value={newTitle}
              onChange={(e)=>setNewTitle(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={1}
              maxRows={6}
              autoFocus
              onKeyDown={(e)=>{ 
                if(e.key==='Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey){ e.preventDefault(); handleAdd(); } 
                if(e.key==='Escape'){ e.preventDefault(); setComposerVisible(false); setNewTitle(''); setDueDate(null); }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '4px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: newTitle ? '#2D2D32' : '#B9B9B9' },
                '& .MuiInputBase-inputMultiline': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: newTitle ? '#2D2D32' : '#B9B9B9' },
                '& .MuiInputBase-input::placeholder': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '16px', color: '#B9B9B9', opacity: 1 }
              }}
            />
            {!!errorMsg && (
              <Typography variant="caption" color="error" sx={{ mt: -0.5 }}>
                {errorMsg}
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              {(() => {
                const today = new Date().toISOString().slice(0,10);
                const tomorrow = (()=>{ const d=new Date(); d.setDate(d.getDate()+1); return d.toISOString().slice(0,10); })();
                // Styl dopasowany do chipów statusu w tabeli ofert (OffersStandaloneTab.tsx)
                const baseChip = {
                  size: 'small' as const,
                  sx: {
                    borderRadius: '4px',
                    minHeight: 21,
                    height: 21,
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0,
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '11px',
                      fontWeight: 400,
                      lineHeight: '13px',
                      color: '#020242',
                    }
                  }
                };
                return (
                  <>
                    <Chip
                      label="Dzisiaj"
                      onClick={()=>{
                        const hasTitle = (newTitle || '').trim().length > 0;
                        if (hasTitle) { handleAdd(today); } else { setDueDate(today); }
                      }}
                      {...baseChip}
                      sx={{
                        ...baseChip.sx,
                        // pastelowy pomarańcz (jak 'druga oferta' w statusach); ciut mocniejszy gdy zaznaczony
                        bgcolor: dueDate===today ? '#FADFBF' : '#FEEED6',
                        color: '#020242',
                      }}
                    />
                    <Chip
                      label="Jutro"
                      onClick={()=>{
                        const hasTitle = (newTitle || '').trim().length > 0;
                        if (hasTitle) { handleAdd(tomorrow); } else { setDueDate(tomorrow); }
                      }}
                      {...baseChip}
                      sx={{
                        ...baseChip.sx,
                        // pastelowy błękit (jak statusy); ciut mocniejszy gdy zaznaczony
                        bgcolor: dueDate===tomorrow ? '#DDE8FF' : '#E6F0FF',
                        color: '#020242',
                      }}
                    />
                  </>
                );
              })()}
              <Tooltip title={dueDate ? `Termin: ${dueDate}` : 'Ustaw termin'}>
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src="/assets/filters/calendar_today.svg"
                    alt="calendar"
                    sx={{ width: 18, height: 18, cursor: 'pointer', opacity: 0.9 }}
                    role="button"
                    tabIndex={0}
                    onClick={() => { 
                      openByKeyboardRef.current = false;
                      setKeyboardPickerOpen(false);
                      try { (dateInputRef.current as any)?.showPicker?.(); } catch { dateInputRef.current?.focus(); dateInputRef.current?.click(); } 
                    }}
                    onKeyDown={(e: any) => {
                      // Enter lub Spacja: otwórz kalendarz. Zapis nastąpi po wyborze daty (onChange).
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openByKeyboardRef.current = true;
                        setKeyboardPickerOpen(true);
                        try { (dateInputRef.current as any)?.showPicker?.(); } catch { dateInputRef.current?.focus(); dateInputRef.current?.click(); }
                      } 
                    }}
                  />
                  {/* Ukryty, ale pozycjonowany input aby natywny date‑picker otwierał się przy ikonie w panelu */}
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={dueDate || ''}
                    onChange={(e)=>{
                      const value = e.target.value || '';
                      if (!value) return;
                      // Jeżeli picker był otwarty klawiaturą i ostatnie wejście nie było pointerem,
                      // traktuj to jako nawigację klawiszami – tylko podgląd, bez zapisu.
                      if (openByKeyboardRef.current && lastInputTypeRef.current !== 'pointer') {
                        setDueDate(value);
                        keyboardChangedRef.current = true;
                        return;
                      }
                      // Klik (pointer) albo tryb myszki – zapisuj od razu
                      const hasTitle = (newTitle || '').trim().length > 0;
                      if (hasTitle) { handleAdd(value); } else { setDueDate(value); }
                    }}
                    onKeyDown={(e)=>{
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = (e.currentTarget as HTMLInputElement).value || '';
                        if (value) {
                          const hasTitle = (newTitle || '').trim().length > 0;
                          if (hasTitle) { handleAdd(value); } else { setDueDate(value); }
                        }
                        openByKeyboardRef.current = false;
                        setKeyboardPickerOpen(false);
                        keyboardChangedRef.current = false;
                      }
                      if (e.key === 'Escape') {
                        openByKeyboardRef.current = false;
                        setKeyboardPickerOpen(false);
                        keyboardChangedRef.current = false;
                      }
                    }}
                    onBlur={()=>{
                      if (openByKeyboardRef.current && keyboardChangedRef.current) {
                        const value = (dateInputRef.current as HTMLInputElement | null)?.value || '';
                        if (value) {
                          const hasTitle = (newTitle || '').trim().length > 0;
                          if (hasTitle) { handleAdd(value); } else { setDueDate(value); }
                        }
                      }
                      openByKeyboardRef.current = false;
                      setKeyboardPickerOpen(false);
                      keyboardChangedRef.current = false;
                    }}
                    tabIndex={-1}
                    style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0, border: 'none', background: 'transparent' }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
          </Box>
        )}
      </Box>
      <>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242' }}
            >
              {(() => {
                const q = (search || '').trim().toLowerCase();
                const count = q ? active.filter(t => (t.title || '').toLowerCase().includes(q)).length : active.length;
                return `Do zrobienia (${count})`;
              })()}
            </Typography>
          </Box>
          <Box sx={{ px: 1.5 }}>
            {(() => {
              const q = (search || '').trim().toLowerCase();
              const list = q ? active.filter(t => (t.title || '').toLowerCase().includes(q)) : active;
              return list;
            })().map(t => (
              <Box
                key={t.id}
                draggable={isDragEnabled}
                onDragStart={(e)=>handleDragStart('active', t.id, e)}
                onDragOver={(e)=>handleDragOver('active', t.id, e)}
                onDrop={(e)=>handleDrop('active', t.id, e)}
                onDragEnd={handleDragEnd}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.2,
                  px: 0.5,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: dragOverId===t.id ? '#F5F5FF' : 'transparent',
                  '&:hover': { backgroundColor: '#F5F5FF' },
                  cursor: dragging && dragging.id===t.id ? 'grabbing' : 'default',
                  '&:active': { cursor: isDragEnabled ? 'grabbing' : 'default' },
                  ...(newlyAddedId===t.id ? {
                    willChange: 'filter, opacity, transform',
                    animation: 'task-add-focus 1000ms ease-out',
                    '@keyframes task-add-focus': {
                      '0%': { transform: 'translateY(-4px) scale(0.98)', opacity: 0, filter: 'blur(8px)' },
                      '60%': { filter: 'blur(2px)', opacity: 0.85, transform: 'translateY(-1px) scale(0.995)' },
                      '100%': { transform: 'translateY(0) scale(1)', opacity: 1, filter: 'blur(0px)' }
                    }
                  } : {})
                }}
              >
                {confettiTaskId === t.id && (
                  <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    {Array.from({ length: 22 }).map((_, i) => {
                      const leftPct = Math.round(Math.random()*100);
                      const dx = Math.round((Math.random()*2 - 1) * 80);
                      const dy = -60 - Math.round(Math.random()*60);
                      const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4'];
                      return (
                        <Box key={`cf-${i}`} sx={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          top: '50%',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: colors[i % colors.length],
                          transform: 'translate(0,0)',
                          opacity: 1,
                          animation: `confetti-pop 820ms ease-out forwards`,
                          animationDelay: `${i*12}ms`,
                          '--dx': `${dx}px`,
                          '--dy': `${dy}px`,
                          '@keyframes confetti-pop': {
                            '0%': { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
                            '100%': { transform: 'translate(var(--dx), var(--dy)) scale(0.9) rotate(360deg)', opacity: 0 }
                          }
                        }} />
                      );
                    })}
                  </Box>
                )}
                <Box sx={{ position: 'relative' }}>
                  <Checkbox checked={false} onClick={()=>handleComplete(t)} />
                  {burstId === t.id && (
                    <Box sx={{
                      position: 'absolute',
                      left: -4,
                      top: -4,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#4040DB',
                      opacity: 0.6,
                      animation: 'burst 420ms ease-out forwards',
                      '@keyframes burst': {
                        '0%': { transform: 'scale(0.5)', opacity: 0.6 },
                        '100%': { transform: 'scale(2.2)', opacity: 0 }
                      }
                    }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {editingTaskId === t.id ? (
                  <TextField
                      value={editingTitle}
                      onChange={(e)=>setEditingTitle(e.target.value)}
                      inputRef={editInputRef}
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={8}
                      fullWidth
                      onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey){ e.preventDefault(); submitEdit(); } if(e.key==='Escape'){ e.preventDefault(); setEditingTaskId(null); setEditingTitle(''); } }}
                      sx={{
                        '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '4px', alignItems: 'flex-start' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                        '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                        '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: editingTitle ? '#2D2D32' : '#B9B9B9', whiteSpace: 'pre-wrap' }
                      }}
                    />
                  ) : (
                    <Stack spacing={0.25} sx={{ alignItems: 'flex-start', width: '100%' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{t.title}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {!!formatDueYmd(t.due) && (() => {
                          const dueYmd = formatDueYmd(t.due)!;
                          // Lista Do zrobienia – spóźnione, jeśli termin wcześniejszy niż dziś
                          const todayYmd = new Date().toISOString().slice(0,10);
                          const isOverdue = dueYmd < todayYmd;
                          const labelColor = isOverdue ? '#C53030' : '#020242';
                          return (
                            <Chip
                              label={dueYmd}
                              size="small"
                              sx={{
                                bgcolor: '#FFF5CC',
                                color: labelColor,
                                borderRadius: '4px',
                                minHeight: 21,
                                height: 21,
                                '& .MuiChip-label': {
                                  px: 1,
                                  py: 0,
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '11px',
                                  fontWeight: 400,
                                  lineHeight: '13px',
                                  color: labelColor
                                }
                              }}
                            />
                          );
                        })()}
                        <Box sx={{ ml: 'auto' }}>
                          {(() => {
                            const a = getAssigner(t.notes);
                            return a ? (<AssignerChip name={a.name} initials={a.initials} />) : null;
                          })()}
                        </Box>
                      </Box>
                      {t.notes && !String(t.notes).startsWith('__ASSIGNER__') && <Typography variant="caption" color="text.secondary">{t.notes}</Typography>}
                    </Stack>
                  )}
                </Box>
                <IconButton size="small" onClick={openMenu(t)} sx={{ ml: 'auto' }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {loading && <Typography variant="body2" sx={{ px: 1, py: 1.5 }}>Ładowanie…</Typography>}
            {!loading && active.length === 0 && (
              <Typography variant="body2" sx={{ px: 1, py: 1.5, color: 'text.secondary' }}>Brak zadań.</Typography>
            )}
          </Box>

          <Box sx={{ px: 2, py: 1, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src="/assets/icons/arrow-right.svg"
              alt="toggle"
              sx={{ width: 10, height: 10, cursor: 'pointer', transform: completedCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 120ms ease' }}
              onClick={()=>setCompletedCollapsed(v=>!v)}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242', cursor: 'pointer' }}
              onClick={()=>setCompletedCollapsed(v=>!v)}
            >
              {(() => {
                const q = (search || '').trim().toLowerCase();
                const count = q ? completed.filter(t => (t.title || '').toLowerCase().includes(q)).length : completed.length;
                return `Ukończone (${count})`;
              })()}
            </Typography>
          </Box>
          {!completedCollapsed && (
          <Box sx={{ px: 1.5, pb: 2 }}>
            {(() => {
              const q = (search || '').trim().toLowerCase();
              const list = q ? completed.filter(t => (t.title || '').toLowerCase().includes(q)) : completed;
              return list;
            })().map(t => (
              <Box
                key={t.id}
                draggable={isDragEnabled}
                onDragStart={(e)=>handleDragStart('completed', t.id, e)}
                onDragOver={(e)=>handleDragOver('completed', t.id, e)}
                onDrop={(e)=>handleDrop('completed', t.id, e)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.2,
                  px: 0.5,
                  py: 0.75,
                  borderRadius: 1,
                  backgroundColor: dragOverId===t.id ? '#F5F5FF' : 'transparent',
                  '&:hover': { backgroundColor: '#F5F5FF' },
                  cursor: dragging && dragging.id===t.id ? 'grabbing' : 'default',
                  '&:active': { cursor: isDragEnabled ? 'grabbing' : 'default' }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Checkbox checked onClick={async ()=> {
                    setBurstId(t.id);
                    setTimeout(()=>setBurstId(null), 420);
                    try {
                      await updateMyTask(t.id, { status: 'needsAction' });
                      // przenieś do listy aktywnych
                      setCompleted(prev => prev.filter(x => x.id !== t.id));
                      setActive(prev => [{ ...t, status: 'needsAction', completed: null }, ...prev]);
                      try { window.dispatchEvent(new CustomEvent('tasks:changed')); } catch {}
                    } catch {}
                  }} />
                  {burstId === t.id && (
                    <Box sx={{
                      position: 'absolute',
                      left: -4,
                      top: -4,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#4040DB',
                      opacity: 0.6,
                      animation: 'burst 420ms ease-out forwards',
                      '@keyframes burst': {
                        '0%': { transform: 'scale(0.5)', opacity: 0.6 },
                        '100%': { transform: 'scale(2.2)', opacity: 0 }
                      }
                    }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {editingTaskId === t.id ? (
                  <TextField
                      value={editingTitle}
                      onChange={(e)=>setEditingTitle(e.target.value)}
                      inputRef={editInputRef}
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={8}
                      fullWidth
                      onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey){ e.preventDefault(); submitEdit(); } if(e.key==='Escape'){ e.preventDefault(); setEditingTaskId(null); setEditingTitle(''); } }}
                      sx={{
                        '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '4px', alignItems: 'flex-start' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                        '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                        '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32', whiteSpace: 'pre-wrap' }
                      }}
                    />
                  ) : (
                    <Stack spacing={0.25} sx={{ alignItems: 'flex-start', width: '100%' }}>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', wordBreak: 'break-word' }}>{t.title}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {!!formatDueYmd(t.due) && (() => {
                          const dueYmd = formatDueYmd(t.due)!;
                          const todayYmd = new Date().toISOString().slice(0,10);
                          const isOverdue = dueYmd < todayYmd;
                          const labelColor = isOverdue ? '#C53030' : '#020242';
                          return (
                            <Chip
                              label={dueYmd}
                              size="small"
                              sx={{
                                bgcolor: '#FFF5CC',
                                color: labelColor,
                                borderRadius: '4px',
                                minHeight: 21,
                                height: 21,
                                '& .MuiChip-label': {
                                  px: 1,
                                  py: 0,
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '11px',
                                  fontWeight: 400,
                                  lineHeight: '13px',
                                  color: labelColor
                                }
                              }}
                            />
                          );
                        })()}
                        <Box sx={{ ml: 'auto' }}>
                          {(() => {
                            const a = getAssigner(t.notes);
                            return a ? (<AssignerChip name={a.name} initials={a.initials} />) : null;
                          })()}
                        </Box>
                      </Box>
                    </Stack>
                  )}
                </Box>
                <IconButton size="small" onClick={openMenu(t)} sx={{ ml: 'auto' }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          )}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            disablePortal
            container={paperRef.current || undefined}
            PaperProps={{ sx: { zIndex: 10 } }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleEdit}>
              <ListItemIcon><Box component="img" src="/assets/icons/edit.svg" alt="edytuj" sx={{ width: 18, height: 18 }} /></ListItemIcon>
              <ListItemText primary="Edytuj" />
            </MenuItem>
            {menuTask && !menuTask.due && (
              <MenuItem onClick={handleAddDue}>
                <ListItemIcon><Box component="img" src="/assets/filters/calendar_today.svg" alt="termin" sx={{ width: 18, height: 18 }} /></ListItemIcon>
                <ListItemText primary="Dodaj termin" />
              </MenuItem>
            )}
            {menuTask && !!menuTask.due && (
              <MenuItem onClick={handleEditDue}>
                <ListItemIcon><Box component="img" src="/assets/filters/calendar_today.svg" alt="termin" sx={{ width: 18, height: 18 }} /></ListItemIcon>
                <ListItemText primary="Edytuj termin" />
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete}>
              <ListItemIcon><Box component="img" src="/assets/icons/delete.svg" alt="usuń" sx={{ width: 16, height: 16 }} /></ListItemIcon>
              <ListItemText primary="Usuń" />
            </MenuItem>
          </Menu>
          {/* Ukryty input do wyboru terminu dla pozycji z menu */}
          <input
            ref={taskDateInputRef}
            type="date"
            style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
            onChange={onPickDueForTask}
          />
          {/* Fallback: prosty popover z natywnym inputem date, jeśli showPicker nie zadziała */}
          <Popover
            open={Boolean(datePopoverAnchor || datePopoverPos)}
            onClose={()=>{ setDatePopoverAnchor(null); setDatePopoverPos(null); setInlineDateTask(null); }}
            anchorEl={datePopoverAnchor}
            anchorReference={datePopoverPos ? 'anchorPosition' : 'anchorEl'}
            anchorPosition={datePopoverPos || undefined}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            disablePortal
            container={paperRef.current || undefined}
            PaperProps={{ sx: { p: 0, zIndex: 20, backgroundColor: 'transparent', boxShadow: 'none' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <input
                ref={popoverInputRef}
                type="date"
                onChange={(e)=>{ 
                  const value = e.target.value || '';
                  const task = inlineDateTask;
                  setInlineDateTask(null);
                  setDatePopoverAnchor(null);
                  setDatePopoverPos(null);
                  if (value) { applyDueForTask(task, value); }
                }}
                style={{ width: 1, height: 1, opacity: 0, border: 'none', padding: 0 }}
                autoFocus
              />
            </Box>
          </Popover>
        </>
    </Drawer>
  );
};

export default TasksDrawer;


